import { checkAddress } from 'legacy/lut/helpers/validateRules/checkAddress'
import { checkAsianCityName } from 'legacy/lut/helpers/validateRules/checkAsianCityName'
import { checkCityName } from 'legacy/lut/helpers/validateRules/checkCityName'
import { checkAsianCompanyName, checkCompanyName } from 'legacy/lut/helpers/validateRules/partner'
import { useMemo } from 'react'

import { ValidatedByCountryFields } from 'clickNgo/configs/validation'
import { BasicInfoFields, PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { asianCountriesWithEspeciallyRequirements } from 'clickNgo/entities/partner/configs/common'
import {
  IPartnerFormBasicRequiredFields,
  IPartnerFormBasicValidate,
} from 'clickNgo/entities/partner/models/form/basic'
import { TPartnerFormKeys } from 'clickNgo/entities/partner/models/form/common'
import { useTaxIdValidate, useUrlValidate, useValidateByCountry } from 'clickNgo/hooks/validation'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'
import { isValidEmail } from 'core-components/library/validation'

import { callValidateByDecision } from '../../../helpers/callValidateByDecision'

interface IUsePartnerBasicInfoValidationParams {
  formKeys: TPartnerFormKeys
}

export const usePartnerBasicInfoValidation = ({
  formKeys,
}: IUsePartnerBasicInfoValidationParams) => {
  const [{ modifiedFields }] = useGetFormData(formKeys.basic)
  const [country] = useGetFormDataField<string>(formKeys.basic, BasicInfoFields.Country)
  const [hierarchyType] = useGetFormDataField<PartnerHierarchyType>(
    formKeys.basic,
    BasicInfoFields.HierarchyType
  )

  const checkCityNameByCountry = useMemo(
    () =>
      asianCountriesWithEspeciallyRequirements.includes(country)
        ? checkAsianCityName
        : checkCityName,
    [country]
  )

  const checkCompanyNameByCountry = useMemo(
    () =>
      asianCountriesWithEspeciallyRequirements.includes(country)
        ? checkAsianCompanyName
        : checkCompanyName,
    [country]
  )

  const { isTaxIdRequired: isTaxIdByRulesRequired, onTaxIdValidate } = useTaxIdValidate({
    country,
    hierarchyType,
  })
  const { isUrlRequired: isUrlByRulesRequired, onUrlValidate } = useUrlValidate({
    country,
    hierarchyType,
  })

  const [onStateValidate, isStateByCountryRequired] = useValidateByCountry({
    countryName: country,
    field: ValidatedByCountryFields.STATE,
  })

  const [onZipCodeValidate, isZipCodeByCountryRequired] = useValidateByCountry({
    countryName: country,
    field: ValidatedByCountryFields.ZIP_CODE,
  })

  const isCommunicationNameRequired = modifiedFields.includes(BasicInfoFields.CommunicationName)
  const isAddressLine1Required = modifiedFields.includes(BasicInfoFields.AddressLine1)
  const isCityRequired = modifiedFields.includes(BasicInfoFields.City)
  const isCountryRequired = modifiedFields.includes(BasicInfoFields.Country)
  const isEmailRequired = modifiedFields.includes(BasicInfoFields.Email)
  const isPhoneCodeRequired = modifiedFields.includes(BasicInfoFields.PhoneCountryCode)
  const isPhoneNumberRequired = modifiedFields.includes(BasicInfoFields.Phone)
  const isStateRequired =
    (modifiedFields.includes(BasicInfoFields.State) ||
      modifiedFields.includes(BasicInfoFields.Country)) &&
    isStateByCountryRequired
  const isTaxIdRequired =
    (modifiedFields.includes(BasicInfoFields.TaxId) ||
      modifiedFields.includes(BasicInfoFields.Country)) &&
    isTaxIdByRulesRequired
  const isUrlRequired =
    (modifiedFields.includes(BasicInfoFields.Url) ||
      modifiedFields.includes(BasicInfoFields.Country)) &&
    isUrlByRulesRequired
  const isZipCodeRequired =
    (modifiedFields.includes(BasicInfoFields.ZipCode) ||
      modifiedFields.includes(BasicInfoFields.Country)) &&
    isZipCodeByCountryRequired

  const basicValidationProps = useMemo<IPartnerFormBasicValidate & IPartnerFormBasicRequiredFields>(
    () => ({
      isCommunicationNameRequired,
      isAddressLine1Required,
      isCityRequired,
      isCountryRequired,
      isEmailRequired,
      isPhoneCodeRequired,
      isPhoneNumberRequired,
      isStateRequired,
      isTaxIdRequired,
      isUrlRequired,
      isZipCodeRequired,
      onAddressLine1Validate: callValidateByDecision(isAddressLine1Required, checkAddress),
      onCityValidate: callValidateByDecision(isCityRequired, checkCityNameByCountry),
      onCommunicationNameValidate: callValidateByDecision(
        isCommunicationNameRequired,
        checkCompanyNameByCountry
      ),
      onEmailValidate: callValidateByDecision(isEmailRequired, isValidEmail),
      onTaxIdValidate: callValidateByDecision(
        modifiedFields.includes(BasicInfoFields.TaxId),
        onTaxIdValidate
      ),
      onUrlValidate: callValidateByDecision(
        modifiedFields.includes(BasicInfoFields.Url),
        onUrlValidate
      ),
      onStateValidate: callValidateByDecision(
        modifiedFields.includes(BasicInfoFields.State),
        onStateValidate
      ),
      onZipCodeValidate: callValidateByDecision(
        modifiedFields.includes(BasicInfoFields.ZipCode),
        onZipCodeValidate
      ),
    }),
    [
      checkCityNameByCountry,
      checkCompanyNameByCountry,
      isAddressLine1Required,
      isCityRequired,
      isCommunicationNameRequired,
      isCountryRequired,
      isEmailRequired,
      isPhoneCodeRequired,
      isPhoneNumberRequired,
      isStateRequired,
      isTaxIdRequired,
      isUrlRequired,
      isZipCodeRequired,
      modifiedFields,
      onStateValidate,
      onTaxIdValidate,
      onUrlValidate,
      onZipCodeValidate,
    ]
  )

  return { basicValidationProps }
}
